import React, { useState, useEffect, useRef } from 'react';
import MentionBox from '../components/MentionBox';
import StickerPicker from '../components/StickerPicker';
import { renderWithStickers, hasSticker } from '../stickers';
import { t } from '../i18n';
import { useParams } from 'react-router-dom';

function renderMessageBody(text) {
  if (hasSticker(text)) {
    return <span dangerouslySetInnerHTML={{ __html: renderWithStickers(text) }} />;
  }
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) => {
    if (/^https?:\/\//.test(part)) {
      const yt = part.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
      if (yt) return <a key={i} href={part} target="_blank" rel="noopener noreferrer" style={{display:'block',marginTop:'4px'}}><img src={`https://img.youtube.com/vi/${yt[1]}/mqdefault.jpg`} style={{width:'180px',borderRadius:'8px'}} alt="yt"/></a>;
      return <a key={i} href={part} target="_blank" rel="noopener noreferrer" style={{color:'#60a5fa',fontSize:'12px',wordBreak:'break-all'}}>{part}</a>;
    }
    return <span key={i}>{part}</span>;
  });
}


function Chat({ user }) {
  const { username } = useParams();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [connected, setConnected] = useState(false);
  const [tipAmount, setTipAmount] = useState(0.1);
  const [showTip, setShowTip] = useState(false);
  const [sending, setSending] = useState(false);
  const [connStatus, setConnStatus] = useState('checking');
  const [otherAvatar, setOtherAvatar] = useState(null);
  const wsRef = useRef(null);
  const bottomRef = useRef(null);
  const pollingRef = useRef(null);
  const usePolling = useRef(false);
  const lastMsgId = useRef(0);

  const fetchMessages = async (initial = false) => {
    try {
      const res = await fetch(`/api/messages/${username}/`, {
        headers: { 'Authorization': `Token ${user.token}` }
      });
      const data = await res.json();
      if (Array.isArray(data)) {
        if (initial) {
          setMessages(data);
        } else {
          setMessages(prev => {
            if (data.length !== prev.length) return data;
            return prev;
          });
        }
        if (data.length > 0) lastMsgId.current = data[data.length - 1].id;
      }
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    if (!user) return;
    // Load messages immediately first
    fetchMessages(true);
    // Check connection status
    fetch(`/api/connections/status/${username}/`, {
      headers: { 'Authorization': `Token ${user.token}` }
    }).then(r => r.json()).then(d => setConnStatus(d.status || 'none'));
    // Fetch other user profile for avatar
    fetch(`/api/profile/${username}/`)
      .then(r => r.json()).then(d => { if (d.avatar) setOtherAvatar(d.avatar); });

    // Try WebSocket first
    try {
      const wsUrl = `wss://${window.location.host}/ws/chat/${username}/?token=${user.token}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => { setConnected(true); usePolling.current = false; };
      ws.onclose = () => {
        setConnected(false);
        // Fallback to polling
        usePolling.current = true;
        pollingRef.current = setInterval(fetchMessages, 3000);
      };
      ws.onerror = () => {
        usePolling.current = true;
        setConnected(true); // show as connected via polling
        pollingRef.current = setInterval(fetchMessages, 3000);
      };
      ws.onmessage = (e) => {
        const data = JSON.parse(e.data);
        if (data.type === 'history') setMessages(data.messages);
        else if (data.type === 'message') setMessages(prev => [...prev, data.message]);
      };
    } catch(e) {
      usePolling.current = true;
      pollingRef.current = setInterval(fetchMessages, 3000);
    }

    return () => {
      if (wsRef.current) wsRef.current.close();
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [username, user]);

  const prevMsgCount = useRef(0);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    // Notify on new message received
    if (messages.length > prevMsgCount.current && prevMsgCount.current > 0) {
      const lastMsg = messages[messages.length - 1];
      if (lastMsg && lastMsg.sender !== user.username) {
        // Vibrate on mobile
        if (navigator.vibrate) navigator.vibrate(200);
        // Update title
        document.title = '💬 New message!';
        setTimeout(() => { document.title = 'PenFlow Chat'; }, 3000);
      }
    }
    prevMsgCount.current = messages.length;
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || sending) return;
    setSending(true);
    const body = input.trim();
    setInput('');

    if (!usePolling.current && wsRef.current && wsRef.current.readyState === 1) {
      wsRef.current.send(JSON.stringify({ type: 'message', body }));
    } else {
      // HTTP fallback
      try {
        await fetch(`/api/messages/${username}/send/`, {
          method: 'POST',
          headers: { 'Authorization': `Token ${user.token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ body })
        });
        await fetchMessages();
      } catch (err) { console.error(err); }
    }
    setSending(false);
  };

  const sendPiTip = async () => {
    if (!window.Pi) { alert(t('ch_pi_browser')); return; }
    try {
      await window.Pi.authenticate(['username', 'payments'], async (payment) => {
        if (payment.transaction?.txid) {
          await fetch('/api/payments/approve/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Token ${user.token}` },
            body: JSON.stringify({ paymentId: payment.identifier })
          });
        }
      });
      await window.Pi.createPayment({
        amount: tipAmount,
        memo: `Pi tip to @${username} via PenFlow Chat`,
        metadata: { type: 'chat_tip', to: username }
      }, {
        onReadyForServerApproval: async (paymentId) => {
          await fetch('/api/payments/approve/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Token ${user.token}` },
            body: JSON.stringify({ paymentId })
          });
        },
        onReadyForServerCompletion: async (paymentId, txid) => {
          await fetch('/api/payments/complete/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Token ${user.token}` },
            body: JSON.stringify({ paymentId, txid })
          });
          // Send tip message
          await fetch(`/api/messages/${username}/send/`, {
            method: 'POST',
            headers: { 'Authorization': `Token ${user.token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ body: `__PI__${tipAmount}` })
          });
          await fetchMessages();
          setShowTip(false);
          alert(`✅ ${tipAmount} Pi sent to @${username}!`);
        },
        onCancel: () => alert(t('ch_payment_cancelled')),
        onError: (err) => console.error(err)
      });
    } catch(err) { console.error(err); }
  };

  if (!user) return (
    <div style={styles.container}>
      <div style={styles.card}><h2 style={{ textAlign: 'center', color: '#6200ea' }}>🔑 Login Required</h2></div>
    </div>
  );

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <a href={`/profile/${username}`} style={styles.backBtn}>←</a>
        <a href={`/profile/${username}`} style={styles.headerUser}>
          {otherAvatar
            ? <img src={otherAvatar} alt={username} style={styles.headerAvatarImg} />
            : <div style={styles.headerAvatar}>{username[0].toUpperCase()}</div>
          }
          <div>
            <div style={styles.headerName}>@{username}</div>
            <div style={{ fontSize: '11px', color: '#10b981' }}>🟢 Active</div>
          </div>
        </a>
        <button style={styles.tipBtn} onClick={() => setShowTip(!showTip)}>💰 Tip</button>
      </div>

      {showTip && (
        <div style={styles.tipPanel}>
          <p style={{ margin: '0 0 8px', fontWeight: 'bold', color: '#6200ea' }}>Send Pi to @{username}</p>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
            {[0.1, 0.5, 1, 5].map(a => (
              <button key={a} style={{ ...styles.tipAmtBtn, backgroundColor: tipAmount === a ? '#6200ea' : '#f3e5f5', color: tipAmount === a ? 'white' : '#6200ea' }}
                onClick={() => setTipAmount(a)}>{a} π</button>
            ))}
          </div>
          <button style={styles.sendTipBtn} onClick={sendPiTip}>💰 Send {tipAmount} Pi</button>
        </div>
      )}

      <div style={styles.messages}>
        {messages.length === 0 && (
          <div style={styles.emptyChat}>
            <p style={{ fontSize: '40px' }}>👋</p>
            <p>Start a conversation with @{username}</p>
          </div>
        )}
        {messages.map((msg, i) => {
          const isMe = msg.sender === user.username;
          const isPi = msg.is_pi || msg.body?.startsWith('__PI__');
          const piAmt = msg.pi_amount || (msg.body?.startsWith('__PI__') ? msg.body.split('__PI__')[1] : null);
          return (
            <div key={i} style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', marginBottom: '8px' }}>
              <div style={{
                ...styles.bubble,
                backgroundColor: isPi ? '#34d399' : isMe ? '#6200ea' : '#f0f0f0',
                color: isPi ? '#000' : isMe ? 'white' : '#333',
                borderRadius: isMe ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
              }}>
                {isPi ? `💰 ${piAmt} Pi sent` : renderMessageBody(msg.body)}
                <div style={{ fontSize: '10px', opacity: 0.7, marginTop: '3px', textAlign: 'right' }}>{msg.created_at}</div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {connStatus !== 'accepted' ? (
        <div style={{ padding: '14px 16px', backgroundColor: '#fff3e0', textAlign: 'center', fontSize: '14px', color: '#0d9488', borderTop: '1px solid #34d399', flexShrink: 0 }}>
          ⚠️ You must be connected to send messages.
          <a href={`/profile/${username}`} style={{ color: '#6200ea', marginLeft: '8px', fontWeight: 'bold' }}>{t('ch_send_request')}</a>
        </div>
      ) : (
        <div style={styles.inputRow}>
          <MentionBox
            style={styles.input}
            placeholder={t('ch_type_message')}
            value={input}
            onChange={setInput}
            onEnter={sendMessage}
          />
          <StickerPicker onPick={(k) => { setInput(`[[sticker:${k}]]`); setTimeout(() => sendMessage(), 30); }} />
          <button style={styles.sendBtn} onClick={sendMessage} disabled={sending}>➤</button>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { display: 'flex', flexDirection: 'column', height: '100vh', maxWidth: '700px', margin: '0 auto', backgroundColor: '#f9f9f9' },
  header: { backgroundColor: '#6200ea', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 },
  backBtn: { color: 'white', textDecoration: 'none', fontSize: '20px', fontWeight: 'bold' },
  headerUser: { display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none', flex: 1 },
  headerAvatar: { width: '38px', height: '38px', borderRadius: '50%', backgroundColor: '#34d399', color: '#6200ea', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '16px' },
  headerAvatarImg: { width: '38px', height: '38px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #34d399' },
  headerName: { color: 'white', fontWeight: 'bold', fontSize: '15px' },
  tipBtn: { backgroundColor: '#34d399', color: '#000', border: 'none', padding: '6px 14px', borderRadius: '15px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' },
  tipPanel: { backgroundColor: '#fff9e6', padding: '14px 16px', borderBottom: '2px solid #34d399', flexShrink: 0 },
  tipAmtBtn: { padding: '6px 12px', borderRadius: '15px', border: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' },
  sendTipBtn: { backgroundColor: '#6200ea', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '20px', cursor: 'pointer', fontWeight: 'bold', width: '100%' },
  messages: { flex: 1, overflowY: 'auto', padding: '16px' },
  emptyChat: { textAlign: 'center', padding: '40px', color: '#999' },
  bubble: { maxWidth: '75%', padding: '10px 14px', fontSize: '14px', lineHeight: '1.4', wordBreak: 'break-word' },
  inputRow: { display: 'flex', gap: '8px', padding: '12px 16px', backgroundColor: 'white', borderTop: '1px solid #eee', flexShrink: 0 },
  input: { flex: 1, padding: '10px 14px', borderRadius: '25px', border: '2px solid #e0e0e0', fontSize: '14px', outline: 'none' },
  sendBtn: { backgroundColor: '#6200ea', color: 'white', border: 'none', width: '44px', height: '44px', borderRadius: '50%', cursor: 'pointer', fontSize: '16px', flexShrink: 0 },
  card: { backgroundColor: 'white', borderRadius: '12px', padding: '30px', margin: '20px' },
};

export default Chat;
